import React, { useMemo, useEffect, useRef, useTransition } from 'react';

import {
	graphql,
	useRefetchableFragment,
	usePreloadedQuery,
	type PreloadedQuery,
} from 'react-relay';
import { useMatch } from '@jira/navigation-apps__routing-hooks/src/useMatch.tsx';
import { filterEdges } from '@jira/servicedesk__customer-service-common/src/filterEdges.tsx';
import { EmptyContentMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/empty-content/index.tsx';
import { Text } from '@atlaskit/primitives/compiled/text';
import { MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { CustomerExperienceMenuContent } from './CustomerExperienceMenuContent.tsx';
import type { CustomerExperiencesListQuery } from './__generated__/CustomerExperiencesListQuery.graphql';
import type { CustomerExperiencesList_currentCustomerExperience$key } from './__generated__/CustomerExperiencesList_currentCustomerExperience.graphql';
import type { CustomerExperiencesListRefetchQuery } from './__generated__/CustomerExperiencesListRefetchQuery.graphql';

export const CustomerExperiencesList = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<CustomerExperiencesListQuery>;
}): React.JSX.Element | null => {
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const match = useMatch();
	const routeHelpCenterId = match.params?.helpCenterId;
	const prevHelpCenterIdRef = useRef<{ value: string | null | undefined }>({
		value: undefined,
	});
	const [_isPending, startTransition] = useTransition();

	const queryData = usePreloadedQuery<CustomerExperiencesListQuery>(
		graphql`
			query CustomerExperiencesListQuery($cloudId: ID!, $workspaceAri: ID!) {
				helpCenter(cloudId: $cloudId) @required(action: THROW) {
					customerExperiences(
						workspaceAri: $workspaceAri
						sortOrder: CREATED_DATE_DESCENDING
						first: 5
					) @required(action: THROW) {
						edges {
							node {
								__typename
								... on HelpCenter {
									id @required(action: THROW)
									type @optIn(to: "HelpCenterTypeTest")
									...CustomerExperienceMenuContent_helpCenter @dangerously_unaliased_fixme
								}
							}
						}
					}
				}
			}
		`,
		queryReference,
	);

	const [currentCustomerExperienceData, refetch] = useRefetchableFragment<
		CustomerExperiencesListRefetchQuery,
		CustomerExperiencesList_currentCustomerExperience$key
	>(
		graphql`
			fragment CustomerExperiencesList_currentCustomerExperience on Query
			@refetchable(queryName: "CustomerExperiencesListRefetchQuery")
			@argumentDefinitions(
				cloudId: { type: "ID!" }
				currentCustomerExperienceAri: { type: "ID!" }
				fetchCurrentCustomerExperience: { type: "Boolean", defaultValue: false }
			) {
				helpCenter(cloudId: $cloudId) @required(action: THROW) {
					currentCustomerExperience: helpCenterById(helpCenterAri: $currentCustomerExperienceAri)
						@include(if: $fetchCurrentCustomerExperience)
						@optIn(to: "HelpCenterAggBeta") {
						__typename
						... on HelpCenter {
							id @required(action: THROW)
							type @optIn(to: "HelpCenterTypeTest")
							...CustomerExperienceMenuContent_helpCenter @dangerously_unaliased_fixme
						}
					}
				}
			}
		`,
		null,
	);

	useEffect(() => {
		if (routeHelpCenterId && routeHelpCenterId !== prevHelpCenterIdRef.current.value) {
			prevHelpCenterIdRef.current.value = routeHelpCenterId;
			const activeHelpCenterAri = `ari:cloud:help::help-center/${activationId}/${routeHelpCenterId}`;

			startTransition(() => {
				refetch(
					{
						cloudId,
						currentCustomerExperienceAri: activeHelpCenterAri,
						fetchCurrentCustomerExperience: true,
					},
					{ fetchPolicy: 'store-and-network' },
				);
			});
		}
	}, [routeHelpCenterId, cloudId, activationId, refetch]);

	const allCustomerExperiences = useMemo(() => {
		const edges = filterEdges(queryData?.helpCenter?.customerExperiences?.edges ?? undefined);
		const top5Experiences = edges
			.filter(
				(
					edge,
				): edge is typeof edge & {
					node: Extract<typeof edge.node, { __typename: 'HelpCenter' }>;
				} => edge.node.__typename === 'HelpCenter' && edge.node.type === 'CUSTOMER_SERVICE',
			)
			.map((edge) => edge.node);

		const currentCustomerExperience =
			currentCustomerExperienceData?.helpCenter?.currentCustomerExperience;
		const allExperiences = [...top5Experiences];

		const currentHelpCenterAri =
			routeHelpCenterId && `ari:cloud:help::help-center/${activationId}/${routeHelpCenterId}`;
		if (
			currentHelpCenterAri &&
			currentCustomerExperience?.__typename === 'HelpCenter' &&
			currentCustomerExperience.type === 'CUSTOMER_SERVICE' &&
			currentCustomerExperience.id === currentHelpCenterAri
		) {
			allExperiences.push(currentCustomerExperience);
		}

		const uniqueExperiences = Array.from(
			new Map(allExperiences.map((exp) => [exp.id, exp])).values(),
		);

		return uniqueExperiences;
	}, [queryData, currentCustomerExperienceData, routeHelpCenterId, activationId]);

	const { formatMessage } = useIntl();

	if (allCustomerExperiences.length === 0) {
		return (
			<EmptyContentMenuItem>
				<Text size="small" color="color.text.subtle">
					{formatMessage(emptyExperiencesMessage)}
				</Text>
			</EmptyContentMenuItem>
		);
	}

	return (
		<>
			<MenuSectionHeading>{formatMessage(recentlyCreatedHeadingMessage)}</MenuSectionHeading>
			{allCustomerExperiences.map((experience) => (
				<CustomerExperienceMenuContent key={experience.id} helpCenterFragment={experience} />
			))}
		</>
	);
};

const emptyExperiencesMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experiences-list.empty-experiences',
	defaultMessage: 'Any customer experience you get access to will show up here.',
	description: "Message shown to user when they don't have any customer experiences",
});

const recentlyCreatedHeadingMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experiences-expandable-menu.recently-created',
	defaultMessage: 'Recently created',
	description: 'Heading for the list of recently created customer experiences in the sidebar',
});
